import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SettingsIcon from '@mui/icons-material/Settings';
import MapIcon from '@mui/icons-material/Map';
import BuildIcon from '@mui/icons-material/Build';
import DealershipHoursDialog from './DealershipHoursDialog';
import useToggle from '../../hooks/useToggle';

// ─── Animations ────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled Components ──────────────────────────────────────────────────────

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    borderRadius: 28,
    background: 'linear-gradient(145deg, #f0f4f8 0%, #e8edf3 100%)',
    boxShadow: '0 32px 80px rgba(0,0,0,0.22)',
    padding: '8px 4px',
    maxWidth: 520,
    width: '100%',
    overflow: 'hidden',
  },
}));

const StyledDialogTitle = styled(DialogTitle)({
  textAlign: 'center',
  fontFamily: "'Nunito', 'Segoe UI', sans-serif",
  fontWeight: 800,
  fontSize: '1.45rem',
  color: '#1e293b',
  letterSpacing: '-0.3px',
  paddingBottom: 12,
  paddingTop: 20,
  '@media (max-width:600px)': {
    fontSize: '1.05rem',
    paddingTop: 12,
    paddingBottom: 8,
  },
});

const ActionTile = styled(Paper)<{ delay?: number }>(({ delay = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '16px 20px',
  borderRadius: 18,
  background: '#ffffff',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  cursor: 'pointer',
  animation: `${fadeUp} 0.4s ease both`,
  animationDelay: `${delay}ms`,
  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
  userSelect: 'none',
  '&:hover': {
    transform: 'translateY(-3px) scale(1.02)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
  },
  '&:active': {
    transform: 'scale(0.98)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  '@media (max-width:600px)': {
    gap: 8,
    padding: '10px 8px',
    borderRadius: 12,
  },
}));

const IconBubble = styled(Box)<{ bgcolor: string }>(({ bgcolor }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: bgcolor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    color: '#ffffff',
    fontSize: 22,
  },
  '@media (max-width:600px)': {
    width: 30,
    height: 30,
    '& svg': {
      fontSize: 14,
    },
  },
}));

const TileLabel = styled(Typography)({
  fontFamily: "'Nunito', 'Segoe UI', sans-serif",
  fontWeight: 700,
  fontSize: '1.05rem',
  color: '#1e293b',
  letterSpacing: '-0.1px',
  '@media (max-width:600px)': {
    fontSize: '0.72rem',
  },
});

const CallButton = styled(Box)<{ component?: React.ElementType }>(() => ({
  width: '100%',
  padding: '16px 24px',
  borderRadius: 4,
  // background: 'linear-gradient(to right, #ECECF3 0%, #ECECF3 100%)',
  background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
  color: '#475569',
  // fontWeight: 600,
  fontSize: '1rem',
  letterSpacing: '0.1px',
  textTransform: 'none',
  textAlign: 'center',
  // boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  '@media (max-width:600px)': {
    padding: '10px 12px',
    fontSize: '0.8rem',
  },
}));

// ─── Data ────────────────────────────────────────────────────────────────────

type tileCode =
  | 'directions'
  | 'hours'
  | 'sales'
  | 'service'
  | 'parts'
  | 'finance';

interface TileConfig {
  code: tileCode;
  label: string;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}

const topTiles: TileConfig[] = [
  {
    code: 'directions',
    label: 'Directions',
    icon: <MapIcon />,
    color: 'linear-gradient(135deg, #4a9fd4 0%, #3b82f6 100%)',
  },
  {
    code: 'hours',
    label: 'Hours',
    icon: <AccessTimeIcon />,
    color: 'linear-gradient(135deg, #5b9bd5 0%, #2563eb 100%)',
  },
];

const bottomTiles: TileConfig[] = [
  {
    code: 'sales',
    label: 'Sales',
    icon: <DirectionsCarIcon />,
    color: 'linear-gradient(135deg, #5b9bd5 0%, #3b82f6 100%)',
  },
  {
    code: 'service',
    label: 'Service',
    icon: <BuildIcon />,
    color: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  },
  {
    code: 'parts',
    label: 'Parts',
    icon: <SettingsIcon />,
    color: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)',
  },
  {
    code: 'finance',
    label: 'Finance',
    icon: <AttachMoneyIcon />,
    color: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
  },
];

// ─── Sub-Components ───────────────────────────────────────────────────────────

const Tile: React.FC<{
  config: TileConfig;
  onClick: () => void;
}> = ({ config, onClick }) => (
  <ActionTile onClick={onClick} elevation={0}>
    <IconBubble bgcolor={config.color}>{config.icon}</IconBubble>
    <TileLabel>{config.label}</TileLabel>
  </ActionTile>
);

// ─── Main Dialog Component ────────────────────────────────────────────────────

interface DealershipDialogProps {
  open: boolean;
  onClose: () => void;
  dealerSettings: any;
  dealershipName?: string;
  handleOpenLocation: () => void;
}

const DealershipContactDialog: React.FC<DealershipDialogProps> = ({
  open,
  onClose,
  dealerSettings,
  dealershipName,
  handleOpenLocation,
}) => {
  const {
    toggle: dealershipHoursToggle,
    onOpen: dealershipHoursOnOpen,
    onClose: dealershipHoursOnClose,
  } = useToggle();

  const handleCall = (phoneNumber?: string) => {
    if (phoneNumber || dealerSettings?.dealershipPhone) {
      window.location.href = `tel:${phoneNumber || dealerSettings?.dealershipPhone}`;
    }
  };

  const handleActionClick = (code: tileCode) => {
    switch (code) {
      case 'directions':
        handleOpenLocation();
        break;
      case 'hours':
        dealershipHoursOnOpen();
        break;
      case 'sales':
        handleCall(dealerSettings?.salesPhoneNumber);
        break;
      case 'service':
        handleCall(dealerSettings?.servicePhoneNumber);
        break;
      case 'parts':
        handleCall(dealerSettings?.partsPhoneNumber);
        break;
      case 'finance':
        handleCall(dealerSettings?.financePhoneNumber);
        break;
    }
  };
  return (
    <>
      <StyledDialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <StyledDialogTitle>Connect With Our Dealership</StyledDialogTitle>

        <Divider sx={{ mx: { xs: 1.5, sm: 3 }, borderColor: '#dde3ec' }} />

        <DialogContent
          sx={{ px: { xs: 1, sm: 2.5 }, py: { xs: 1.25, sm: 2.5 } }}
        >
          {/* Top row — Directions & Hours */}
          <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 2 }}>
            {topTiles.map((tile, _i) => (
              <Grid size={6} key={tile.label}>
                <Tile
                  config={tile}
                  onClick={() => handleActionClick(tile.code)}
                />
              </Grid>
            ))}
          </Grid>

          {/* Click to Call */}
          <CallButton sx={{ mb: 1.5 }}>Click to Call Dealership</CallButton>

          {/* Bottom grid — Sales, Service, Parts, Finance */}
          <Grid container spacing={{ xs: 1, sm: 1.5 }}>
            {bottomTiles.map((tile) => (
              <Grid size={6} key={tile.label}>
                <Tile
                  config={tile}
                  onClick={() => handleActionClick(tile.code)}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </StyledDialog>
      {dealershipHoursToggle && (
        <DealershipHoursDialog
          open={dealershipHoursToggle}
          onClose={dealershipHoursOnClose}
          hours={JSON.parse(dealerSettings?.dealershipHours || '{}')}
          dealershipName={dealershipName}
        />
      )}
    </>
  );
};

export default DealershipContactDialog;
